/**
 * CFS homepage behaviour (build spec §8).
 * - <cfs-postcode-check>: hero postcode check. Saves the postcode for the basket (same key as the
 *   header pill) and says which delivery options apply, using the "Local delivery postcode
 *   districts" theme setting.
 * - <cfs-lazy-row>: loads a product row from the Section Rendering API when it nears the viewport.
 * - <cfs-reorder>: "Add last order to basket" for signed-in customers (spec §14.2): one
 *   /cart/add.js request with the in-stock lines rendered by snippets/cfs-welcome-back.liquid.
 * - <cfs-product-strip>: the hero's slow, endless strip of best sellers (concept H). The cards are
 *   picked and shuffled by an inline script before first paint; this repeats them once for the
 *   loop (copies hidden from screen readers and the tab order) and moves the strip with a CSS
 *   animation; shoppers can still swipe or scroll it. Pauses on hover, focus, touch and when off
 *   screen, has a pause button (WCAG 2.2.2), and does not move for people who turn off animations.
 *   "+" adds one to the basket through assets/cfs-cart.js.
 * - <cfs-tile-pics>: fills a homepage tile with the newest products' photos when it nears the
 *   screen (sections/cfs-collection-pics.liquid).
 */
import { addVariants, setVariantTotal, quantityForVariant, CART_STATE_EVENT } from '@theme/cfs-cart';
const POSTCODE_KEY = 'cfs:postcode';
const UK_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/i;

class CfsPostcodeCheck extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('form');
    const input = /** @type {HTMLInputElement} */ (this.querySelector('input'));
    const result = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-postcode-result]'));
    const districts = (this.dataset.localPostcodes ?? '').toUpperCase().split(/[\s,]+/).filter(Boolean);

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const match = UK_POSTCODE.exec(input.value.trim());
      if (!match) {
        result.textContent = 'Please enter a full UK postcode, for example RM3 7AB.';
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      const postcode = `${match[1]} ${match[2]}`.toUpperCase();
      try {
        localStorage.setItem(POSTCODE_KEY, postcode);
      } catch {}
      for (const label of document.querySelectorAll('[data-cfs-postcode-label]')) label.textContent = postcode;
      document.dispatchEvent(new CustomEvent('cfs:postcode', { detail: { postcode } }));
      result.textContent = districts.includes(match[1].toUpperCase())
        ? `Good news: 1-hour delivery and click & collect are available for ${postcode}.`
        : `We deliver to ${postcode} by Royal Mail or DHL. Free on £70+ to the UK mainland (first parcel up to 15kg).`;
    });
  }
}

class CfsLazyRow extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return void this.#load();
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        this.#load();
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(this);
  }

  async #load() {
    const url = this.dataset.url;
    const limit = Number(this.dataset.limit) || 6;
    const track = this.querySelector('[data-cfs-row-track]');
    if (!url || !track) return;
    try {
      const html = await (await fetch(url)).text();
      const items = [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-cfs-row-items] > li')].slice(0, limit);
      if (items.length === 0) {
        this.closest('.cfs-row-section')?.setAttribute('hidden', '');
        return;
      }
      track.replaceChildren(...items.map((li) => document.importNode(li, true)));
    } catch {
      this.closest('.cfs-row-section')?.setAttribute('hidden', '');
    }
  }
}

class CfsReorder extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-cfs-reorder]')?.addEventListener('click', this.#add);
  }

  #add = async () => {
    const status = this.querySelector('[data-cfs-reorder-status]');
    const skipped = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-reorder-skipped]'));
    /** @type {{ id: number, quantity: number }[]} */
    let items = [];
    try {
      items = JSON.parse(this.dataset.items ?? '[]');
    } catch {}
    if (!items.length || this.classList.contains('cfs-reorder--busy')) return;

    this.classList.add('cfs-reorder--busy');
    if (status) status.textContent = 'Adding your last order…';
    try {
      await addVariants({ items, source: this });
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      if (status) status.textContent = `Added ${count} ${count === 1 ? 'item' : 'items'} to your basket.`;
      /** @type {any} */ (document.querySelector('cart-drawer-component')?.closest('theme-drawer'))?.open?.();
    } catch (error) {
      if (status) status.textContent = /** @type {Error} */ (error).message || 'Could not add your last order. Please try again.';
    } finally {
      this.classList.remove('cfs-reorder--busy');
      if (skipped) skipped.hidden = false;
    }
  };
}

const STRIP_SPEED = 30; // px per second (22 on phones)
const STRIP_RESUME_MS = 2500; // after the shopper stops scrolling the strip themselves
const NOTE_MS = 2200; // the "N in basket" note on a strip card; the strip moves on when it goes

/*
 * Movement is a CSS animation of the track (transform, run by the compositor): no script or
 * scroll events every frame. Scrolling the strip by script made Elfsight and the message app
 * re-measure the page on every frame (blocking time 11.5 s → 2.2 s on a slowed phone).
 * While the shopper swipes, scrolls or tabs through cards the strip becomes a normal scroller
 * at the same spot, then hands back to the animation.
 */
class CfsProductStrip extends HTMLElement {
  /** @type {HTMLElement | null} */ #viewport = null;
  /** @type {HTMLElement | null} */ #list = null;
  /** @type {HTMLButtonElement | null} */ #toggle = null;
  /** @type {HTMLElement | null} */ #status = null;
  #period = 0; // width of one set of cards: the loop length
  #baseWidth = 0; // the cards' width before the copies went in, until the loop length is known
  #copySets = 0;
  #scrolled = false; // the viewport has scrolled at least once (until then its scroll position is 0)
  #pausedByShopper = false;
  /** @type {Set<string>} */ #holds = new Set(); // standing still: hover, offscreen
  /** @type {Set<string>} */ #manual = new Set(); // the shopper moves it: swipe/wheel, keyboard focus
  #resumeTimer = 0;
  #addedTimer = 0;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {ResizeObserver | undefined} */ #resize;
  /** @type {IntersectionObserver | undefined} */ #visible;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-cfs-strip-viewport]');
    this.#list = this.querySelector('[data-cfs-strip-list]');
    this.#toggle = this.querySelector('[data-cfs-strip-toggle]');
    this.#status = this.querySelector('[data-cfs-strip-status]');
    if (!this.#viewport || !this.#list || this.#list.children.length < 3) return;

    this.addEventListener('click', this.#onClick);
    document.addEventListener(CART_STATE_EVENT, this.#syncCards);
    queueMicrotask(this.#syncCards); // after the loop copies below are made
    if (this.#reduced.matches) return; // a plain, swipeable row

    this.#list.addEventListener('mousedown', this.#onCopyPress);
    // Setting up the loop reads no layout while the page loads (PageSpeed "forced reflow", 22 Oct 2026):
    // the copies go in on the first ResizeObserver call, and the loop length comes from the strip's
    // widths before and after them, as the observer reports them.
    this.#copySets = 0;
    this.#baseWidth = 0;
    this.#resize = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (!width) return; // not shown yet
      if (!this.#copySets) {
        this.#baseWidth = width;
        this.#copySets = -1; // the copies go in on the next frame, so other scripts measuring now get a laid-out page
        const screen = innerWidth; // read now, while the layout is up to date
        requestAnimationFrame(() => (this.#copySets = this.#repeatCards(width, screen)));
      } else if (this.#copySets < 0) {
        return;
      } else if (this.#baseWidth) {
        this.#setPeriod((width - this.#baseWidth) / this.#copySets);
        this.#baseWidth = 0;
      } else this.#measure(); // a later resize
    });
    this.#resize.observe(this.#list);
    this.#visible = new IntersectionObserver(([entry]) => this.#hold('offscreen', !entry.isIntersecting));
    this.#visible.observe(this);

    // Stable handler references: the element can be reconnected (e.g. moved in the theme editor).
    this.addEventListener('pointerenter', this.#onPointer);
    this.addEventListener('pointerleave', this.#onPointer);
    this.addEventListener('focusin', this.#onFocus);
    this.addEventListener('focusout', this.#onFocus);
    for (const type of ['pointerdown', 'wheel', 'touchstart']) {
      this.#viewport.addEventListener(type, this.#onShopperScroll, { passive: true });
    }
    this.#viewport.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#viewport.addEventListener('pointerdown', this.#onDragStart);
    this.#list.addEventListener('dragstart', (event) => event.preventDefault()); // no ghost image of a photo or link
    this.#reduced.addEventListener('change', this.#update);
    if (this.#toggle) {
      this.#toggle.hidden = false;
      this.#toggle.addEventListener('click', this.#onToggle);
    }
    this.#update();
  }

  /** @param {PointerEvent} event */
  #onPointer = (event) => {
    if (event.pointerType === 'mouse') this.#hold('hover', event.type === 'pointerenter');
  };

  /** @param {FocusEvent} event */
  #onFocus = (event) => {
    const into = /** @type {Node | null} */ (event.type === 'focusin' ? event.target : event.relatedTarget);
    // A focused card must be scrollable into view; the pause button has its own say. Keyboard focus only:
    // a tap on + also focuses it, and that hold would keep the strip still until the shopper tapped
    // elsewhere (the add has its own short pause, below).
    const onCard = Boolean(into && this.contains(into) && into !== this.#toggle && /** @type {Element} */ (into).matches?.(':focus-visible'));
    if (onCard === this.#manual.has('focus')) return;
    if (onCard) this.#manual.add('focus');
    else this.#manual.delete('focus');
    this.#update();
    if (onCard) /** @type {HTMLElement} */ (into).scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  };

  /** Play means play: the pointer and focus are on the button, so drop those holds too. */
  #onToggle = () => {
    this.#pausedByShopper = !this.#pausedByShopper;
    if (!this.#pausedByShopper) {
      this.#holds.delete('hover');
      this.#manual.delete('focus');
    }
    this.#update();
  };

  /**
   * Copies are aria-hidden, so a pressed button or link in one must not take focus (Chrome blocks
   * aria-hidden on a focused element's ancestor). The click itself still goes through.
   * @param {MouseEvent} event
   */
  #onCopyPress = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest?.('[data-cfs-copy] a, [data-cfs-copy] button')) event.preventDefault();
  };

  disconnectedCallback() {
    clearTimeout(this.#resumeTimer);
    clearTimeout(this.#addedTimer);
    document.removeEventListener(CART_STATE_EVENT, this.#syncCards);
    this.#resize?.disconnect();
    this.#visible?.disconnect();
  }

  /**
   * Hidden copies of the cards follow the originals, so the strip can loop without a gap: one set,
   * or more when a short strip is narrower than the screen.
   * @param {number} baseWidth - the cards' width without copies
   * @param {number} screen - the window's width
   * @returns {number} how many sets of copies went in
   */
  #repeatCards(baseWidth, screen) {
    const list = /** @type {HTMLElement} */ (this.#list);
    for (const old of list.querySelectorAll('[data-cfs-copy]')) old.remove(); // reconnected, e.g. moved in the theme editor
    const originals = [...list.children];
    const appendCopies = () => {
      for (const card of originals) {
        const copy = /** @type {HTMLElement} */ (card.cloneNode(true));
        copy.setAttribute('aria-hidden', 'true');
        copy.dataset.cfsCopy = '';
        for (const el of copy.querySelectorAll('a, button')) /** @type {HTMLElement} */ (el).tabIndex = -1;
        list.append(copy);
      }
    };
    appendCopies();
    // Enough sets to cover one loop plus the screen (no wider than the window)
    let sets = 1;
    for (; sets < 4 && (sets + 1) * baseWidth < baseWidth + screen; sets++) appendCopies();
    return sets;
  }

  #measure() {
    const list = /** @type {HTMLElement} */ (this.#list);
    const first = /** @type {HTMLElement | null} */ (list.firstElementChild);
    const firstCopy = /** @type {HTMLElement | null} */ (list.querySelector('[data-cfs-copy]'));
    this.#setPeriod(first && firstCopy ? firstCopy.offsetLeft - first.offsetLeft : 0);
  }

  /** @param {number} period */
  #setPeriod(period) {
    if (period === this.#period) return;
    this.#stopAnimation(); // restarts below with the new loop length (and the speed for this screen)
    this.#period = period;
    this.#update();
  }

  /** @param {string} reason @param {boolean} on */
  #hold(reason, on) {
    if (on) this.#holds.add(reason);
    else this.#holds.delete(reason);
    this.#update();
  }

  #speed() {
    return innerWidth < 990 ? STRIP_SPEED * 0.75 : STRIP_SPEED;
  }

  /** Hands over from the animation to the scroll position, keeping the strip where it is. */
  #stopAnimation() {
    const list = /** @type {HTMLElement} */ (this.#list);
    if (!list.classList.contains('is-moving')) return;
    const offset = -new DOMMatrixReadOnly(getComputedStyle(list).transform).m41;
    list.classList.remove('is-moving', 'is-held');
    /** @type {HTMLElement} */ (this.#viewport).scrollLeft = offset;
    this.#scrolled = true;
  }

  /** Hands over from the scroll position to the animation, from the same spot. */
  #startAnimation() {
    const list = /** @type {HTMLElement} */ (this.#list);
    const viewport = /** @type {HTMLElement} */ (this.#viewport);
    const left = this.#scrolled ? viewport.scrollLeft : 0; // not asked before any scroll: a read forces layout
    const offset = ((left % this.#period) + this.#period) % this.#period;
    const speed = this.#speed();
    list.style.setProperty('--strip-period', `${this.#period}px`);
    list.style.setProperty('--strip-duration', `${(this.#period / speed).toFixed(2)}s`);
    list.style.setProperty('--strip-delay', `${(-offset / speed).toFixed(2)}s`);
    if (left) viewport.scrollLeft = 0;
    list.classList.add('is-moving');
  }

  #update = () => {
    const list = /** @type {HTMLElement} */ (this.#list);
    const animate = !this.#reduced.matches && this.#period > 0 && this.#manual.size === 0;
    if (animate && !list.classList.contains('is-moving')) this.#startAnimation();
    else if (!animate) this.#stopAnimation();
    list.classList.toggle('is-held', this.#pausedByShopper || this.#holds.size > 0);
    if (this.#toggle) {
      this.#toggle.classList.toggle('is-paused', this.#pausedByShopper);
      this.#toggle.setAttribute('aria-label', this.#pausedByShopper ? 'Play moving products' : 'Pause moving products');
    }
  };

  /** The shopper is scrolling the strip: hand over to scrolling, then carry on from where they leave it. */
  #onShopperScroll = () => {
    if (!this.#manual.has('shopper')) {
      this.#manual.add('shopper');
      this.#update();
    }
    this.#resumeLater();
  };

  #resumeLater() {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      this.#manual.delete('shopper');
      this.#update();
    }, STRIP_RESUME_MS);
  }

  // Click-and-drag with a mouse (owner, 10 Oct 2026): touch already swipes and trackpads scroll; a mouse
  // needs dragging. A drag of more than a few pixels doesn't count as a click on the card under it.
  /** @type {{ x: number, left: number, moved: boolean } | null} */
  #drag = null;

  /** @param {PointerEvent} event */
  #onDragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.#period <= 0) return;
    if (/** @type {Element} */ (event.target).closest?.('button')) return; // + keeps its click
    this.#onShopperScroll(); // stops the motion and hands over to scrolling
    const viewport = /** @type {HTMLElement} */ (this.#viewport);
    this.#drag = { x: event.clientX, left: viewport.scrollLeft, moved: false };
    window.addEventListener('pointermove', this.#onDragMove);
    window.addEventListener('pointerup', this.#onDragEnd, { once: true });
  };

  /** @param {PointerEvent} event */
  #onDragMove = (event) => {
    const drag = this.#drag;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 6) return;
    if (!drag.moved) {
      drag.moved = true;
      this.classList.add('is-dragging');
    }
    // Endless both ways: wrap by one set of cards (the loop copies are identical)
    let next = drag.left - dx;
    if (next >= this.#period) {
      next -= this.#period;
      drag.left -= this.#period;
    } else if (next < 0) {
      next += this.#period;
      drag.left += this.#period;
    }
    /** @type {HTMLElement} */ (this.#viewport).scrollLeft = next;
    this.#resumeLater();
  };

  #onDragEnd = () => {
    window.removeEventListener('pointermove', this.#onDragMove);
    const moved = this.#drag?.moved;
    this.#drag = null;
    this.classList.remove('is-dragging');
    // The click that ends a drag must not open the card under the pointer
    if (moved) {
      const swallow = (/** @type {Event} */ event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      this.addEventListener('click', swallow, { capture: true, once: true });
      // A drag released outside the strip sends no click here: don't leave it waiting for the next one
      window.setTimeout(() => this.removeEventListener('click', swallow, { capture: true }), 0);
    }
    this.#resumeLater();
  };

  #onScroll = () => {
    const viewport = /** @type {HTMLElement} */ (this.#viewport);
    this.#scrolled = true;
    if (!this.#manual.has('shopper') || this.#drag) return;
    this.#resumeLater();
    // Scrolled past the first set by hand: jump back to the same spot in it.
    if (this.#period > 0 && viewport.scrollLeft >= this.#period) viewport.scrollLeft -= this.#period;
  };

  // Basket quantities (owner, 10 Oct 2026): the strip is a quick add, so it keeps its single +, now with a
  // count of what is in the basket on it. The loop repeats its cards, so every copy shows the same count.
  /** @type {Map<string, { wanted: number, confirmed: number, timer: number, inFlight: boolean }>} */
  #pending = new Map();

  #syncCards = () => {
    for (const card of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cfs-strip-card[data-variant-id]'))) {
      const variantId = card.dataset.variantId ?? '';
      const pending = this.#pending.get(variantId);
      if (pending && (pending.timer || pending.inFlight)) continue; // its own change is on the way
      this.#renderCard(card, quantityForVariant(variantId));
    }
  };

  /** @param {string} variantId @param {number} quantity */
  #renderVariant(variantId, quantity) {
    for (const card of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(`.cfs-strip-card[data-variant-id="${variantId}"]`))) {
      this.#renderCard(card, quantity);
    }
  }

  /** Writes only what changed (apps observe the whole page). @param {HTMLElement} card @param {number} quantity */
  #renderCard(card, quantity) {
    const add = /** @type {HTMLElement | null} */ (card.querySelector('[data-cfs-strip-add]'));
    if (!add) return; // products with options link to their page
    const name = card.querySelector('.cfs-strip-card__name')?.textContent?.trim() || 'item';
    // The quick + stays (owner): a count on it says how many are already in the basket
    const count = quantity > 0 ? String(quantity) : '';
    if ((add.dataset.count ?? '') !== count) {
      if (count) add.dataset.count = count;
      else delete add.dataset.count;
    }
    const label = quantity > 0 ? `Add another ${name} (${quantity} in basket)` : `Add ${name} to basket`;
    if (add.getAttribute('aria-label') !== label) add.setAttribute('aria-label', label);
    card.classList.toggle('is-in-basket', quantity > 0);
    const atMax = quantity >= this.#max(card);
    if ((add.getAttribute('aria-disabled') === 'true') !== atMax) {
      if (atMax) add.setAttribute('aria-disabled', 'true');
      else add.removeAttribute('aria-disabled');
    }
  }

  /** Stock on hand of a tracked variant that stops selling at 0 (data-max), or Infinity. @param {HTMLElement} card */
  #max(card) {
    const max = card.dataset.max;
    return max === undefined || max === '' ? Infinity : Math.max(0, Number(max));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLElement | null} */ (target.closest('[data-cfs-strip-add]'));
    const card = /** @type {HTMLElement | null} */ (button?.closest('.cfs-strip-card') ?? null);
    const variantId = card?.dataset.variantId;
    if (!button || !card || !variantId) return;
    const pending = this.#pending.get(variantId);
    const shown = pending ? pending.wanted : quantityForVariant(variantId);
    const next = shown + 1;
    const max = this.#max(card);
    if (next > shown && next > max) {
      this.#note(card, max === 0 ? 'Sorry, this is sold out' : `All ${max} we have ${max === 1 ? 'is' : 'are'} in your basket`, true);
      return;
    }
    this.#request(card, variantId, next, shown);
  };

  /** @param {HTMLElement} card @param {string} variantId @param {number} quantity @param {number} shown */
  #request(card, variantId, quantity, shown) {
    const pending = this.#pending.get(variantId) ?? { wanted: shown, confirmed: quantityForVariant(variantId), timer: 0, inFlight: false };
    this.#pending.set(variantId, pending);
    pending.wanted = quantity;
    this.#renderVariant(variantId, quantity);
    // Instant feedback on the card tapped, and the strip holds still while the "N in basket" note shows,
    // so a second tap lands on the same card; it moves on as the note goes (owner, 10 Oct 2026). With a
    // mouse, the hover hold still keeps it still while the pointer is over the cards.
    this.#note(card, `${quantity} in basket`, false);
    if (!this.#reduced.matches) {
      this.#manual.add('added');
      this.#update();
      clearTimeout(this.#addedTimer);
      this.#addedTimer = window.setTimeout(() => {
        this.#manual.delete('added');
        this.#update();
      }, NOTE_MS);
    }
    clearTimeout(pending.timer);
    // The first add goes straight away; quick repeat taps are coalesced into one request
    const delay = pending.confirmed === 0 && quantity === 1 ? 0 : 300;
    pending.timer = window.setTimeout(() => {
      pending.timer = 0;
      this.#flush(card, variantId);
    }, delay);
  }

  /** @param {HTMLElement} card @param {string} variantId */
  async #flush(card, variantId) {
    const pending = this.#pending.get(variantId);
    if (!pending || pending.inFlight || pending.wanted === pending.confirmed) return;
    const name = card.querySelector('.cfs-strip-card__name')?.textContent?.trim() || 'Item';
    const target = pending.wanted;
    pending.inFlight = true;
    try {
      const cart = await setVariantTotal({ variantId, total: target, source: card });
      pending.confirmed = quantityForVariant(variantId, cart);
      if (pending.wanted === target) {
        this.#renderVariant(variantId, pending.confirmed);
        if (pending.confirmed !== target) this.#note(card, `Only ${pending.confirmed} available`, true);
        this.#announce(pending.confirmed > 0 ? `${name}: ${pending.confirmed} in your basket.` : `Removed ${name} from your basket.`);
      }
    } catch (error) {
      pending.confirmed = quantityForVariant(variantId);
      if (pending.wanted === target) {
        pending.wanted = pending.confirmed;
        this.#renderVariant(variantId, pending.confirmed);
      }
      const message = /** @type {Error} */ (error)?.message || 'Could not update your basket';
      this.#note(card, message, true);
      this.#announce(message);
    } finally {
      pending.inFlight = false;
      if (pending.wanted !== pending.confirmed && !pending.timer) this.#flush(card, variantId);
      else if (!pending.timer) this.#pending.delete(variantId);
    }
  }

  /** @param {HTMLElement} card @param {string} text @param {boolean} isError */
  #note(card, text, isError) {
    card.dataset.note = text;
    card.classList.toggle('is-added', !isError);
    card.classList.toggle('is-error', isError);
    clearTimeout(Number(card.dataset.noteTimer));
    card.dataset.noteTimer = String(
      window.setTimeout(() => {
        delete card.dataset.note;
        card.classList.remove('is-added', 'is-error');
      }, isError ? 4000 : NOTE_MS)
    );
  }

  /** @param {string} text */
  #announce(text) {
    if (!this.#status) return;
    this.#status.textContent = '';
    window.setTimeout(() => this.#status && (this.#status.textContent = text), 50);
  }
}

class CfsTilePics extends HTMLElement {
  connectedCallback() {
    const load = async () => {
      if (!this.dataset.url) return;
      try {
        const html = await (await fetch(this.dataset.url)).text();
        const pics = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cfs-pics]');
        if (pics) this.replaceChildren(...[...pics.children].map((node) => document.importNode(node, true)));
      } catch {}
    };
    if (!('IntersectionObserver' in window)) return void load();
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        load();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(this);
  }
}

if (!customElements.get('cfs-product-strip')) customElements.define('cfs-product-strip', CfsProductStrip);
if (!customElements.get('cfs-tile-pics')) customElements.define('cfs-tile-pics', CfsTilePics);
if (!customElements.get('cfs-postcode-check')) customElements.define('cfs-postcode-check', CfsPostcodeCheck);
if (!customElements.get('cfs-lazy-row')) customElements.define('cfs-lazy-row', CfsLazyRow);
if (!customElements.get('cfs-reorder')) customElements.define('cfs-reorder', CfsReorder);
